import { useEffect, useState } from 'react';
import { Card, Form, Input, Button, Typography, Alert, Space } from 'antd';
import { LockOutlined, MailOutlined, SafetyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth, type AdminSession } from './store';

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '/v1';

interface HealthPayload {
  status: 'ok' | 'degraded';
  dependencies?: {
    postgres?: { reachable: boolean; detail?: string };
    redis?: { reachable: boolean; detail?: string };
  };
}

/** Ping /health on mount so the user sees "what's actually wrong" before
 *  bothering with the password. The /health endpoint now always returns
 *  200 with a structured payload — we mine it for which dependency is
 *  unreachable so the banner says precisely "Redis is down" or "Postgres
 *  is down", not just a generic 500. */
async function probeApi(): Promise<{ reachable: boolean; detail: string }> {
  try {
    // 2s timeout — /health does a SELECT 1 with its own 500ms cap, so a
    // healthy API answers in <50ms. Any longer means the API isn't there
    // or its event loop is wedged.
    const res = await axios.get<HealthPayload>(`${BASE_URL}/health`, { timeout: 2000 });
    if (typeof res.data !== 'object' || res.data === null) {
      return {
        reachable: false,
        detail: 'Got a non-JSON response from /health. The API may be down and Vite is serving its fallback HTML page.',
      };
    }
    if (res.data.status === 'ok') return { reachable: true, detail: '' };
    const issues: string[] = [];
    const deps = res.data.dependencies;
    if (deps?.postgres && !deps.postgres.reachable) {
      issues.push(`Postgres: ${deps.postgres.detail ?? 'unreachable'}`);
    }
    if (deps?.redis && !deps.redis.reachable) {
      issues.push(`Redis: ${deps.redis.detail ?? 'unreachable'}`);
    }
    return {
      reachable: false,
      detail:
        issues.length > 0
          ? `API is up but degraded. ${issues.join(' · ')}`
          : 'API reports degraded status.',
    };
  } catch (e) {
    const err = e as { code?: string; message?: string };
    // Map common axios error codes to actionable copy. The unbranded
    // `err.code` strings ("ECONNABORTED", "ERR_NETWORK") aren't helpful
    // to a non-developer operator.
    let detail: string;
    switch (err.code) {
      case 'ECONNABORTED':
        detail = `Timed out waiting for the API at ${BASE_URL} (2s). Either it isn't running or it's wedged.`;
        break;
      case 'ECONNREFUSED':
      case 'ERR_NETWORK':
        detail = `No process listening at ${BASE_URL}. Start the API: double-click Start-API.bat in the repo root, or run pnpm --filter @maja/api dev.`;
        break;
      case 'ERR_BAD_RESPONSE':
        detail = `The API returned an error response. If you're going through Vite's proxy, the upstream API is likely down.`;
        break;
      default:
        detail = err.code ?? err.message ?? 'unknown';
    }
    return { reachable: false, detail };
  }
}

/**
 * Admin login — email + password (+ TOTP if 2FA-enrolled).
 *
 * On success: stores the session token (sent as `Authorization: Bearer` on
 * every subsequent request, valid until `expiresAt`) AND the profile (used
 * by AppLayout to gate the sidebar entries by role).
 */
export function LoginPage() {
  const navigate = useNavigate();
  const signIn = useAuth((s) => s.signIn);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [needsTotp, setNeedsTotp] = useState(false);
  const [apiDown, setApiDown] = useState<string | null>(null);

  // Probe the API once on mount. If it's down, surface a clear banner
  // before the user types anything — saves a round-trip of "why didn't
  // my password work?".
  useEffect(() => {
    let cancelled = false;
    void probeApi().then((r) => {
      if (cancelled) return;
      setApiDown(r.reachable ? null : r.detail);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onFinish(values: { email: string; password: string; totpCode?: string }) {
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await axios.post<AdminSession>(`${BASE_URL}/admin/auth/login`, {
        email: values.email.trim(),
        password: values.password,
        ...(values.totpCode ? { totpCode: values.totpCode.trim() } : {}),
      });
      signIn(data);
      navigate('/', { replace: true });
    } catch (e) {
      const err = e as {
        response?: {
          status?: number;
          data?: { code?: string; message?: string; fields?: { retryAfterSeconds?: string } };
        };
        request?: unknown;
        message?: string;
        code?: string;
      };
      const code = err.response?.data?.code;
      const status = err.response?.status;
      if (code === 'TOTP_REQUIRED') {
        setNeedsTotp(true);
        setError('Enter your TOTP code to finish signing in.');
        return;
      }
      // Distinguish "server reachable but rejected us" from "server not
      // reachable at all" — the user needs to know whether to retry the
      // password or restart the API.
      let msg: string;
      if (err.code === 'ERR_NETWORK' || err.code === 'ECONNREFUSED' || !err.response) {
        msg =
          'Could not reach the API at http://localhost:4000. ' +
          'Is the API terminal running? (pnpm --filter @maja/api dev)';
      } else if (status === 500) {
        // 500 with a JSON body → surface the message. 500 with HTML
        // (Vite proxy fallback when target is down) → guide them to the
        // API terminal.
        msg = err.response?.data?.message
          ? `Server error: ${err.response.data.message}`
          : 'API returned 500 with no JSON body. Check the API terminal for the stack trace.';
      } else if (status === 429) {
        // Per-IP limit (1 minute), or this account is locked after repeated
        // wrong passwords — that one says how long.
        const wait = Number(err.response?.data?.fields?.retryAfterSeconds);
        const minutes = wait > 0 ? Math.ceil(wait / 60) : 1;
        msg = `Too many sign-in attempts. Try again in ${minutes} minute${minutes === 1 ? '' : 's'}.`;
      } else if (status === 401 || status === 403) {
        msg = err.response?.data?.message ?? 'Invalid username or password.';
      } else {
        msg = err.response?.data?.message ?? `Unexpected error (HTTP ${status ?? '?'}).`;
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#0A0A0A',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 32,
      }}
    >
      <Card style={{ maxWidth: 420, width: '100%' }}>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <Space>
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: 12,
                background: 'linear-gradient(135deg,#FB3C7F,#FF6B4A)',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 800,
              }}
            >
              D
            </div>
            <Typography.Title level={3} style={{ margin: 0 }}>
              Dude Admin
            </Typography.Title>
          </Space>
          <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
            Sign in with your admin email and password. TOTP is requested only
            if your account has 2FA enrolled.
          </Typography.Paragraph>
          {apiDown && (
            <Alert
              type="error"
              showIcon
              message="API is not reachable"
              description={
                <span style={{ fontSize: 12 }}>
                  {apiDown}
                  <br />
                  <br />
                  Start it in a terminal:
                  <br />
                  <code>cd D:\Project\video_chat\maja</code>
                  <br />
                  <code>pnpm --filter @maja/api dev</code>
                  <br />
                  <br />
                  Then refresh this page.
                </span>
              }
            />
          )}
          {error && <Alert type="error" message={error} showIcon />}
          <Form layout="vertical" onFinish={onFinish} autoComplete="on">
            <Form.Item
              label="Username or email"
              name="email"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input
                prefix={<MailOutlined />}
                placeholder="spikeconnect"
                autoComplete="username"
              />
            </Form.Item>
            <Form.Item
              label="Password"
              name="password"
              rules={[{ required: true, message: 'Required' }]}
            >
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </Form.Item>
            {needsTotp ? (
              <Form.Item
                label="TOTP code"
                name="totpCode"
                rules={[{ required: true, message: 'Required' }]}
              >
                <Input
                  prefix={<SafetyOutlined />}
                  placeholder="6-digit code"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={8}
                />
              </Form.Item>
            ) : null}
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              disabled={!!apiDown}
              block
              size="large"
            >
              Sign in
            </Button>
          </Form>
        </Space>
      </Card>
    </div>
  );
}
